import {
  useState, useEffect, useMemo, useRef,
} from 'react';
import {
  Heading,
  Container,
  Grid,
  Box,
  Text,
  useTheme,
  Flex,
  UnorderedList,
  ListItem,
  Link,
  HStack,
  VStack,
  Menu,
  MenuButton,
  MenuList,
  MenuOptionGroup,
  MenuItemOption,
  Button,
  Checkbox,
  Radio,
  Icon,
  Stack,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  DrawerHeader,
  DrawerBody,
  useDisclosure,
} from '@chakra-ui/react';
import { FiChevronDown } from 'react-icons/fi';
import Chart from 'react-apexcharts';
import { isEmpty } from 'lodash';
import { useRecoilValue } from 'recoil';
import { currentDatabaseSelector } from '@transport-insights/uikit';
import { currencyFormatter } from '@src/utils/math';
import MainLayout from '@src/components/layouts/main';
import EmptyState from '@src/components/shared/EmptyState';
import LoadingSpinner from '@src/components/shared/LoadingSpinner';
import { useMaintenanceCostComparisons } from '@src/modules/performance/context/reports-api-hooks';
import MoreInfoDrawer from '@src/components/shared/MoreInfoDrawer';

const getWcPrefix = (wc = '') => wc.split('-')[0]?.trim().toLowerCase();
const calculateAverageYearRange = (latestLockYear) => {
  const numberOfYears = 3;
  if (!latestLockYear) return null;
  const endYear = parseInt(latestLockYear.split('/')[0], 10);
  const startYear = endYear - numberOfYears + 1;
  const endFinancialYear = latestLockYear;
  const startFinancialYear = `${startYear}/${startYear - 1999}`;
  return `${startFinancialYear} to ${endFinancialYear}`;
};
const calculateMetricAverage = (items, metricKey) => {
  const metricValues = items
    .map((item) => item?.[metricKey])
    .filter((value) => Number.isFinite(value));
  if (!metricValues.length) return null;
  return metricValues.reduce((acc, value) => acc + value, 0) / metricValues.length;
};
const getPeerMetricValue = (items, peerMetricKey) => {
  const peerValues = items
    .map((item) => item?.[peerMetricKey])
    .filter((value) => Number.isFinite(value));
  return peerValues.length ? peerValues[0] : null;
};

const sortOptions = [
  { label: 'Name A-Z', value: 'nameAsc' },
  { label: 'Name Z-A', value: 'nameDesc' },
  { label: 'Dollars High to Low', value: 'valueDesc' },
  { label: 'Dollars Low to High', value: 'valueAsc' },
];

const metricOptions = [
  { label: 'Dollars per km', value: 'dollarsPerKm' },
  { label: 'Dollars per lane km', value: 'dollarsPerLaneKm' },
  { label: 'Dollars per Million VKT', value: 'dollarsPerMillionVKT' },
];
const orderSelectedMetrics = (metrics) => metricOptions
  .map((item) => item.value)
  .filter((metric) => metrics.includes(metric));

const metricColorMap = {
  dollarsPerKm: { color: 'blue', shade: 800 },
  dollarsPerLaneKm: { color: 'blue', shade: 600 },
  dollarsPerMillionVKT: { color: 'blue', shade: 300 },
};

const orangeShadeMap = {
  dollarsPerKm: 'orange.600',
  dollarsPerLaneKm: 'orange.400',
  dollarsPerMillionVKT: 'orange.200',
};

function MaintenanceOperations() {
  const { data, isLoading, error } = useMaintenanceCostComparisons();
  const rca = useRecoilValue(currentDatabaseSelector);
  const theme = useTheme();
  const { isOpen: isNetworksOpen, onOpen: onNetworksOpen, onClose: onNetworksClose } = useDisclosure();
  const errorStatus = error?.response?.status;

  const wcOptions = [...new Set(data?.map((x) => x.wc))].sort();
  const peerGroups = useMemo(() => {
    const groups = [...new Set(data?.map((x) => x.peerGroup))].sort();
    if (!groups.includes('all')) groups.unshift('all');
    return groups;
  }, [data]);

  const [selectedMetrics, setSelectedMetrics] = useState(metricOptions.map((item) => item.value));
  const [selectedOption, setSelectedOption] = useState(undefined);
  const [selectedPeerGroup, setSelectedPeerGroup] = useState('all');
  const [sortBy, setSortBy] = useState('nameAsc');
  const [selectedNetworks, setSelectedNetworks] = useState([]);
  const hasInitializedNetworksRef = useRef(false);
  const previousSelectedPeerGroupRef = useRef(selectedPeerGroup);
  const [filteredData, setFilteredData] = useState([]);
  const [totalValues, setTotalValues] = useState({
    peerAverageDollarsPerKm: 0,
    peerAverageDollarsPerLaneKm: 0,
    peerAverageDollarsPerMillionVKT: 0,
    rcaDollarsPerKm: 0,
    rcaDollarsPerLaneKm: 0,
    rcaDollarsPerMillionVKT: 0,
  });
  const networkOptions = useMemo(() => {
    if (!selectedOption || !data) return [];
    const wcPrefix = getWcPrefix(selectedOption);
    return data
      .filter((item) => getWcPrefix(item.wc) === wcPrefix)
      .filter((item, index, items) => items.findIndex((x) => x.id === item.id) === index)
      .sort((a, b) => a.la.localeCompare(b.la));
  }, [data, selectedOption]);
  const networkColumns = useMemo(() => {
    const columnCount = 2;
    const itemsPerColumn = Math.ceil(networkOptions.length / columnCount) || 1;

    return Array.from({ length: columnCount }, (_, index) => networkOptions.slice(
      index * itemsPerColumn,
      (index + 1) * itemsPerColumn,
    )).filter((column) => column.length);
  }, [networkOptions]);
  const seriesKeyMap = {
    dollarsPerKm: 'dollarsPerKm',
    dollarsPerLaneKm: 'dollarsPerLaneKm',
    dollarsPerMillionVKT: 'dollarsPerMillionVKT',
  };

  const seriesLabelMap = {
    dollarsPerKm: 'Dollars per km',
    dollarsPerLaneKm: 'Dollars per lane km',
    dollarsPerMillionVKT: 'Dollars per Million VKT',
  };

  const averageByMetricMap = {
    dollarsPerKm: totalValues.peerAverageDollarsPerKm,
    dollarsPerLaneKm: totalValues.peerAverageDollarsPerLaneKm,
    dollarsPerMillionVKT: totalValues.peerAverageDollarsPerMillionVKT,
  };

  const rcaByMetricMap = {
    dollarsPerKm: totalValues.rcaDollarsPerKm,
    dollarsPerLaneKm: totalValues.rcaDollarsPerLaneKm,
    dollarsPerMillionVKT: totalValues.rcaDollarsPerMillionVKT,
  };

  const chartSeries = selectedMetrics.flatMap((metric) => {
    const metricColor = metricColorMap[metric];
    const color = theme.colors.brand[metricColor.color][metricColor.shade];
    const orangeShade = theme.colors.brand[orangeShadeMap[metric].split('.')[0]][orangeShadeMap[metric].split('.')[1]];
    const averageValue = averageByMetricMap[metric];

    return [
      {
        name: seriesLabelMap[metric],
        type: 'bar',
        color,
        data: filteredData.map((item) => ({
          x: item.la,
          y: item[seriesKeyMap[metric]],
          fillColor:
            item.id === rca.id
              ? orangeShade
              : color,
        })),
      },
      {
        name: `Average ${seriesLabelMap[metric]}`,
        type: 'line',
        color,
        data: filteredData.map((item) => ({
          x: item.la,
          y: Number.isFinite(averageValue) ? averageValue : null,
        })),
      },
    ];
  });

  const chartStrokeWidth = chartSeries.map((series) => (series.type === 'line' ? 2 : 0));
  const chartStrokeDashArray = chartSeries.map((series) => (series.type === 'line' ? 6 : 0));
  const legendMarkerCustomHtml = chartSeries.map((series) => () => (
    series.type === 'line'
      ? '<span style="display:inline-block;width:16px;height:0;border-top:2px dashed currentColor;vertical-align:middle;"></span>'
      : '<span style="display:inline-block;width:10px;height:10px;background:currentColor;border-radius:2px;vertical-align:middle;"></span>'
  ));
  const legendMarkerSizes = chartSeries.map((series) => (series.type === 'line' ? 16 : 10));

  // Validate WC selection
  useEffect(() => {
    if (!wcOptions.length) return;
    const valid = wcOptions.find((x) => getWcPrefix(x) === getWcPrefix(selectedOption));
    if (!valid) setSelectedOption(wcOptions[0]);
  }, [wcOptions, selectedOption]);

  // Validate Peer Group
  useEffect(() => {
    if (peerGroups.length <= 1) return; // 'all' only
    if (!peerGroups.includes(selectedPeerGroup)) {
      setSelectedPeerGroup('all');
    }
  }, [peerGroups, selectedPeerGroup]);

  useEffect(() => {
    if (hasInitializedNetworksRef.current) return;
    if (selectedPeerGroup !== 'all' || !networkOptions.length) return;
    setSelectedNetworks(networkOptions.map((item) => item.id));
    hasInitializedNetworksRef.current = true;
  }, [networkOptions, selectedPeerGroup]);

  useEffect(() => {
    const selectedPeerGroupChanged = previousSelectedPeerGroupRef.current !== selectedPeerGroup;

    if (selectedPeerGroup === 'all' && networkOptions.length && selectedPeerGroupChanged) {
      setSelectedNetworks(networkOptions.map((item) => item.id));
    }

    previousSelectedPeerGroupRef.current = selectedPeerGroup;
  }, [networkOptions, selectedPeerGroup]);

  // Filter + sort data
  useEffect(() => {
    if (!selectedOption || !selectedPeerGroup || !data) return;

    const wcPrefix = getWcPrefix(selectedOption);
    const filteredByWc = data.filter((x) => getWcPrefix(x.wc) === wcPrefix);
    const filteredByPeer = selectedPeerGroup === 'all'
      ? filteredByWc
      : filteredByWc.filter((x) => x.peerGroup === selectedPeerGroup);
    const availableNetworkIds = networkOptions.map((item) => item.id);
    const selectedNetworkIds = selectedNetworks
      .filter((id) => availableNetworkIds.includes(id));
    const activeNetworkIds = selectedNetworkIds.length ? selectedNetworkIds : availableNetworkIds;
    const filteredByNetwork = selectedPeerGroup === 'all'
      ? filteredByPeer.filter((item) => activeNetworkIds.includes(item.id))
      : filteredByPeer;

    const getMaxSelectedMetricValue = (item) => selectedMetrics.reduce((max, metric) => {
      const value = item[seriesKeyMap[metric]];
      if (!Number.isFinite(value)) return max;
      return Math.max(max, value);
    }, Number.NEGATIVE_INFINITY);

    const sorted = [...filteredByNetwork].sort((a, b) => {
      switch (sortBy) {
        case 'nameAsc':
          return a.la.localeCompare(b.la);

        case 'nameDesc':
          return b.la.localeCompare(a.la);

        case 'valueDesc':
          return getMaxSelectedMetricValue(b) - getMaxSelectedMetricValue(a);

        case 'valueAsc':
          return getMaxSelectedMetricValue(a) - getMaxSelectedMetricValue(b);

        default:
          return 0;
      }
    });

    const isNationalSelection = selectedPeerGroup === 'all';
    const peerAverageDollarsPerKm = isNationalSelection
      ? calculateMetricAverage(filteredByWc, 'dollarsPerKm')
      : getPeerMetricValue(filteredByPeer, 'peerDollarsPerKm');
    const peerAverageDollarsPerLaneKm = isNationalSelection
      ? calculateMetricAverage(filteredByWc, 'dollarsPerLaneKm')
      : getPeerMetricValue(filteredByPeer, 'peerDollarsPerLaneKm');
    const peerAverageDollarsPerMillionVKT = isNationalSelection
      ? calculateMetricAverage(filteredByWc, 'dollarsPerMillionVKT')
      : getPeerMetricValue(filteredByPeer, 'peerDollarsPerMillionVKT');

    const rcaDollarsPerKm = filteredByWc.find((x) => x.id === rca.id)?.dollarsPerKm;
    const rcaDollarsPerLaneKm = filteredByWc.find((x) => x.id === rca.id)?.dollarsPerLaneKm;
    const rcaDollarsPerMillionVKT = filteredByWc.find((x) => x.id === rca.id)?.dollarsPerMillionVKT;

    setTotalValues({
      peerAverageDollarsPerKm,
      peerAverageDollarsPerLaneKm,
      peerAverageDollarsPerMillionVKT,
      rcaDollarsPerKm,
      rcaDollarsPerLaneKm,
      rcaDollarsPerMillionVKT,
    });

    setFilteredData(
      sorted.map((item) => ({
        ...item,
      })),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOption, selectedPeerGroup, sortBy, data, rca?.id, selectedMetrics, selectedNetworks, networkOptions]);

  if (isLoading) {
    return (
      <MainLayout>
        <LoadingSpinner />
      </MainLayout>
    );
  }

  if (errorStatus === 401 || errorStatus === 500) {
    return (
      <MainLayout>
        <EmptyState
          title="Access Denied"
          message={(
            <>
              <Text color="gray.400">You do not have permission to view this page.</Text>
              <Text color="gray.400">Please sign in or select a different RCA.</Text>
            </>
          )}
        />
      </MainLayout>
    );
  }

  if (!isLoading && data && isEmpty(data)) {
    return (
      <MainLayout>
        <EmptyState
          title="No Data"
          message={(
            <>
              <Text color="gray.400">Sorry there is no data available for the selected network and year.</Text>
              <Text color="gray.400">Please try a different year and/or network.</Text>
            </>
          )}
        />
      </MainLayout>
    );
  }

  return (
    <MainLayout backgroundColor="gray.50">
      <Container maxW="full">
        <Flex justify="space-between" align="center" mb={2}>
          <Heading mb={2}>Maintenance Operations & Renewal</Heading>
          <MoreInfoDrawer title="More Information" size="lg">
            <Text mb={4}>
              The page shows the costs per kilometre of network, costs per lane km and costs per million VKT for the selected Work Category (WC) code, for all Territorial Local Authorities (TLAs) in the selected Peer Group. To see the costs for all TLAs, select &apos;All&apos;.
            </Text>
            <Text mb={4}>
              The TLA you have currently selected at the top of Transport Insights will show in orange, the other TLAs will show in blue.
            </Text>
            <Text mb={4}>
              If you select the Total Costs option instead of an individual Work Category code, the costs reported per TLA are the sum of the following Work Category codes: 111, 112, 113, 114, 121, 122, 123, 124, 131, 151, 161, 211, 212, 213, 214, 215, 221, 222, and 231.
            </Text>
            <Text mb={4}>
              The data for this page has been provided by NZ Transport Agency.
            </Text>
            <Text fontWeight="bold" mb={4}>Useful links:</Text>
            <UnorderedList>
              <ListItem>
                <Link
                  href="https://www.nzta.govt.nz/planning-and-investment/planning-and-investment-knowledge-base/202124-nltp/2021-24-nltp-activity-classes-and-work-categories/"
                  target="_blank"
                >
                  2021–24 NLTP activity classes and work categories
                </Link>
              </ListItem>
              <ListItem>
                <Link
                  href="https://www.nzta.govt.nz/planning-and-investment/planning-and-investment-knowledge-base/2024-27-nltp-investment-requirements/2024-27-nltp-activity-classes-and-work-categories/"
                  target="_blank"
                >
                  2024–27 NLTP activity classes and work categories
                </Link>
              </ListItem>
            </UnorderedList>
          </MoreInfoDrawer>
        </Flex>

        {/* WC options grid */}
        <Grid
          gap={2}
          templateColumns={{ base: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)', lg: 'repeat(6, 1fr)' }}
        >
          {wcOptions.map((item) => {
            const isSelected = getWcPrefix(item) === getWcPrefix(selectedOption);
            return (
              <Box
                key={item}
                background="white"
                border="2px solid"
                borderColor={isSelected ? 'brand.orange.400' : 'transparent'}
                color={isSelected ? 'brand.orange.400' : 'gray.800'}
                shadow="base"
                borderRadius="md"
                p={2}
                _hover={{
                  backgroundColor: isSelected ? null : 'brand.orange.50',
                  cursor: isSelected ? null : 'pointer',
                }}
                onClick={() => {
                  setSelectedOption(item);
                  window.scrollTo(0, 0);
                }}
                transition="all 0.2s"
              >
                <Text fontSize="md" fontWeight="bold" color="inherit" mb={1} lineHeight="1">{item.split('-')[0]}</Text>
                <Text fontSize="12px" color="gray.600" lineHeight="1">
                  {item.split('-')[1]}
                </Text>
              </Box>
            );
          })}
        </Grid>

        {/* Summary Boxes */}
        <Grid
          my={4}
          gap={2}
          templateColumns={{ base: 'repeat(1, 1fr)', md: 'repeat(3, 1fr)' }}
        >
          {metricOptions.map((metric) => {
            const averageValue = averageByMetricMap[metric.value];
            const rcaValue = rcaByMetricMap[metric.value];
            return (
              <Box
                key={metric.value}
                p={3}
                borderRadius="md"
                background="white"
                shadow="base"
              >
                <VStack align="start" spacing={1}>
                  <Text fontSize="xs" fontWeight="bold" mb={1} textTransform="uppercase" textAlign="left">
                    {metric.label}
                  </Text>
                  <HStack
                    width="100%"
                    spacing={2}
                    align="center"
                    justify="space-between"
                  >
                    <Box>
                      <Text fontSize="2xl" fontWeight="bold" textAlign="left">
                        {!Number.isFinite(rcaValue)
                          ? 'No Data'
                          : currencyFormatter.format(rcaValue)}
                      </Text>
                      <Text fontSize="xs" textAlign="left">{rca?.name}</Text>
                    </Box>
                    <Box>
                      <Text fontSize="2xl" fontWeight="bold" textAlign="right">
                        {!Number.isFinite(averageValue)
                          ? 'No Data'
                          : currencyFormatter.format(averageValue)}
                      </Text>
                      <Text fontSize="xs" textAlign="right">
                        {selectedPeerGroup === 'all' ? 'National' : selectedPeerGroup}
                        {' '}
                        Average
                      </Text>
                    </Box>
                  </HStack>
                </VStack>
              </Box>
            );
          })}
        </Grid>

        {/* Chart */}
        <Box
          background="white"
          shadow="base"
          borderRadius="md"
        >
          <Stack
            spacing={4}
            align={{ base: 'start', lg: 'center' }}
            justify="space-between"
            mb={6}
            p={4}
            borderBottom="1px solid"
            borderColor="gray.100"
            direction={{ base: 'column', xl: 'row' }}
          >
            {/* Left: filter menus */}
            <Stack
              spacing={2}
              align={{ base: 'start', lg: 'center' }}
              direction={{ base: 'column', lg: 'row' }}
            >
              {/* Metric Toggle Menu */}
              <Menu closeOnSelect={false}>
                <MenuButton
                  as={Button}
                  size="md"
                  variant="unstyled"
                  p={3}
                  borderRadius="base"
                  border="1px solid"
                  borderColor="gray.200"
                  background="white"
                  shadow="base"
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  fontSize="sm"
                  _hover={{ backgroundColor: 'gray.50' }}
                  _active={{ backgroundColor: 'gray.100' }}
                  _focus={{ boxShadow: 'outline' }}
                  textAlign="left"
                  rightIcon={<Icon as={FiChevronDown} boxSize={4} />}
                >
                  Metrics:
                  <Box as="span" fontWeight="normal" ml={1}>
                    {orderSelectedMetrics(selectedMetrics).map((metric) => seriesLabelMap[metric]).join(', ') || 'None selected'}
                  </Box>
                </MenuButton>
                <MenuList>
                  <MenuOptionGroup
                    type="checkbox"
                    value={selectedMetrics}
                    onChange={(nextMetrics) => setSelectedMetrics(orderSelectedMetrics(Array.isArray(nextMetrics) ? nextMetrics : [nextMetrics]))}
                  >
                    {metricOptions.map((item) => (
                      <MenuItemOption
                        key={item.value}
                        value={item.value}
                        icon={null}
                      >
                        <Checkbox
                          isChecked={selectedMetrics.includes(item.value)}
                          pointerEvents="none"
                        >
                          {item.label}
                        </Checkbox>
                      </MenuItemOption>
                    ))}
                  </MenuOptionGroup>
                </MenuList>
              </Menu>

              {/* Peer Group Filter Menu */}
              <Menu>
                <MenuButton
                  as={Button}
                  size="md"
                  variant="unstyled"
                  p={3}
                  borderRadius="base"
                  border="1px solid"
                  borderColor="gray.200"
                  background="white"
                  shadow="base"
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  fontSize="sm"
                  _hover={{ backgroundColor: 'gray.50' }}
                  _active={{ backgroundColor: 'gray.100' }}
                  _focus={{ boxShadow: 'outline' }}
                  textAlign="left"
                  rightIcon={<Icon as={FiChevronDown} boxSize={4} />}
                >
                  Group:
                  <Box as="span" fontWeight="normal" ml={1}>
                    {selectedPeerGroup === 'all' ? 'All Networks' : selectedPeerGroup}
                  </Box>
                </MenuButton>
                <MenuList>
                  <MenuOptionGroup type="radio" value={selectedPeerGroup} onChange={setSelectedPeerGroup}>
                    {peerGroups.map((x) => (
                      <MenuItemOption
                        key={x}
                        value={x}
                        icon={null}
                      >
                        <Radio
                          isChecked={selectedPeerGroup === x}
                          pointerEvents="none"
                        >
                          {x === 'all' ? 'All Networks' : x}
                        </Radio>
                      </MenuItemOption>
                    ))}
                  </MenuOptionGroup>
                </MenuList>
              </Menu>

              {selectedPeerGroup === 'all' && (
                <Button
                  size="md"
                  variant="unstyled"
                  p={3}
                  borderRadius="base"
                  border="1px solid"
                  borderColor="gray.200"
                  background="white"
                  shadow="base"
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  fontSize="sm"
                  _hover={{ backgroundColor: 'gray.50' }}
                  _active={{ backgroundColor: 'gray.100' }}
                  _focus={{ boxShadow: 'outline' }}
                  textAlign="left"
                  rightIcon={<Icon as={FiChevronDown} boxSize={4} />}
                  onClick={onNetworksOpen}
                >
                  Networks:
                  <Box as="span" fontWeight="normal" ml={1}>
                    {selectedNetworks.length}
                    {' '}
                    selected
                  </Box>
                </Button>
              )}
            </Stack>

            {/* Right: sort menu */}
            <Menu>
              <MenuButton
                as={Button}
                size="md"
                variant="unstyled"
                p={3}
                borderRadius="base"
                border="1px solid"
                borderColor="gray.200"
                background="white"
                shadow="base"
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                fontSize="sm"
                _hover={{ backgroundColor: 'gray.50' }}
                _active={{ backgroundColor: 'gray.100' }}
                _focus={{ boxShadow: 'outline' }}
                textAlign="left"
                rightIcon={<Icon as={FiChevronDown} boxSize={4} />}
              >
                Order by:
                <Box as="span" fontWeight="normal" ml={1}>
                  {sortOptions.find((opt) => opt.value === sortBy)?.label}
                </Box>
              </MenuButton>
              <MenuList zIndex="popover">
                <MenuOptionGroup type="radio" value={sortBy} onChange={setSortBy}>
                  {sortOptions.map((item) => (
                    <MenuItemOption
                      key={item.value}
                      value={item.value}
                      icon={null}
                    >
                      <Radio
                        isChecked={sortBy === item.value}
                        pointerEvents="none"
                      >
                        {item.label}
                      </Radio>
                    </MenuItemOption>
                  ))}
                </MenuOptionGroup>
              </MenuList>
            </Menu>
          </Stack>
          <Drawer isOpen={isNetworksOpen} placement="right" onClose={onNetworksClose} size="lg">
            <DrawerOverlay />
            <DrawerContent>
              <DrawerCloseButton />
              <DrawerHeader>
                <HStack spacing={2}>
                  <Button
                    size="sm"
                    colorScheme="gray"
                    variant="outline"
                    onClick={() => setSelectedNetworks(networkOptions.map((item) => item.id))}
                  >
                    Select all
                  </Button>
                  <Button
                    size="sm"
                    colorScheme="gray"
                    variant="outline"
                    onClick={() => setSelectedNetworks(rca?.id ? [rca.id] : [])}
                  >
                    Deselect all
                  </Button>
                </HStack>
              </DrawerHeader>
              <DrawerBody>
                <HStack align="flex-start" spacing={4}>
                  {networkColumns.map((column, columnIndex) => (
                    <VStack key={`network-column-${columnIndex}`} flex={1} align="stretch" spacing={1}>
                      {column.map((item) => {
                        const isCurrentNetwork = item.id === rca.id;
                        const isChecked = isCurrentNetwork || selectedNetworks.includes(item.id);

                        return (
                          <Checkbox
                            key={item.id}
                            size="sm"
                            isChecked={isChecked}
                            isDisabled={isCurrentNetwork}
                            onChange={(event) => {
                              if (isCurrentNetwork) return;
                              const { checked } = event.target;
                              setSelectedNetworks((current) => {
                                const currentSet = new Set(current);
                                if (checked) {
                                  currentSet.add(item.id);
                                } else {
                                  currentSet.delete(item.id);
                                }
                                if (rca?.id) currentSet.add(rca.id);
                                return networkOptions
                                  .map((network) => network.id)
                                  .filter((networkId) => currentSet.has(networkId));
                              });
                            }}
                            whiteSpace="normal"
                            alignItems="center"
                          >
                            {item.la}
                          </Checkbox>
                        );
                      })}
                    </VStack>
                  ))}
                </HStack>
              </DrawerBody>
            </DrawerContent>
          </Drawer>
          <Box p={4}>
            <Chart
              type="bar"
              height="470"
              width="100%"
              options={{
                title: {
                  text: `${selectedOption} - 3 year average (${calculateAverageYearRange(data?.[0]?.lockYear)})`,
                },
                dataLabels: { enabled: false },
                legend: {
                  position: 'top',
                  showForSingleSeries: false,
                  onItemClick: {
                    toggleDataSeries: false,
                  },
                  markers: {
                    customHTML: legendMarkerCustomHtml,
                    size: legendMarkerSizes,
                  },
                },
                yaxis: [
                  {
                    title: { text: 'Dollars ($)' },
                    labels: {
                      formatter(value) {
                        return currencyFormatter.format(value);
                      },
                    },
                  },
                ],
                xaxis: {
                  title: { text: selectedPeerGroup !== 'all' ? selectedPeerGroup : '' },
                  labels: { show: true, rotate: -45, hideOverlappingLabels: false },
                },
                colors: selectedMetrics.map((metric) => {
                  const metricColor = metricColorMap[metric];
                  return theme.colors.brand[metricColor.color][metricColor.shade];
                }),
                tooltip: {
                  enabled: true,
                  shared: true,
                  intersect: false,
                  marker: { show: false },
                  y: { formatter: (value) => (Number.isFinite(value) ? currencyFormatter.format(value) : 'No Data') },
                },
                plotOptions: { bar: { distributed: false, horizontal: false } },
                markers: {
                  size: 0,
                  hover: {
                    size: 0,
                  },
                },
                stroke: {
                  width: chartStrokeWidth,
                  dashArray: chartStrokeDashArray,
                  dropShadow: {
                    enabled: true,
                    top: 1,
                    left: 0,
                    blur: 2,
                    opacity: 0.25,
                  },
                },
              }}
              series={chartSeries}
            />
          </Box>
        </Box>
      </Container>
    </MainLayout>
  );
}

export default MaintenanceOperations;
